import { useState } from 'react';
import useSWR, { mutate as revalidate } from 'swr';
import { useTranslation } from 'react-i18next';
import Input, { Label } from 'src/components/FormComponents/Input';
import { EnvelopeIcon, PhoneIcon } from 'src/components/Icons/icons';
import { FormDialog } from 'src/components/Modals/FormDialog';
import { useNotification } from 'src/components/Notification/useNotfication';
import container from 'src/container';
import { dispatch } from 'src/state';
import { UserThunks } from 'src/state/thunks';
import { ChangeContactPanel } from './ChangeContactPanel';
import { Contact, ContactKind, formatContact } from './accessErrors';

const {
    cradle: { authService },
} = container;

const KINDS: ContactKind[] = ['phone', 'email'];

type LoginContactFieldsProps = {
    userId: string;
    /** Support mode shows the values without change links. */
    readOnly?: boolean;
};

/**
 * Phone and email on My profile. Each opens a dialog to add, change or confirm it
 * with a code sent to the new contact.
 */
export const LoginContactFields = ({
    userId,
    readOnly,
}: LoginContactFieldsProps) => {
    const { t } = useTranslation();
    const notify = useNotification();
    const [editing, setEditing] = useState<ContactKind>();
    const { data, mutate } = useSWR(['account-contacts', userId], () =>
        authService.accountAccess<{
            contacts: Contact[];
            unconfirmed: Contact[];
        }>('contacts', undefined, true),
    );

    const confirmed = (kind: ContactKind) =>
        data?.contacts.find(item => item.kind === kind);
    const unconfirmed = (kind: ContactKind) =>
        confirmed(kind)
            ? undefined
            : data?.unconfirmed.find(item => item.kind === kind);

    const handleSaved = async (contact: Contact) => {
        setEditing(undefined);
        await mutate();
        // Refresh other places showing the signed-in person's phone and email.
        await revalidate(userId);
        await dispatch(UserThunks.refreshUser());
        notify(
            t('accountAccess.contacts.saved', {
                contact: formatContact(contact),
            }),
        );
    };

    const actionLabel = (kind: ContactKind) =>
        t(
            confirmed(kind)
                ? 'accountAccess.contacts.change'
                : unconfirmed(kind)
                ? 'accountAccess.contacts.confirmAction'
                : 'accountAccess.contacts.add',
        );

    return (
        <>
            {KINDS.map(kind => {
                const shown = confirmed(kind) ?? unconfirmed(kind);
                const label = t(
                    kind === 'phone'
                        ? 'profile.labels.phone'
                        : 'profile.labels.email',
                );
                return (
                    <div key={kind}>
                        <Label label={label} />
                        <div className="relative">
                            <Input
                                className="text-input pr-24"
                                type={kind === 'phone' ? 'tel' : 'email'}
                                leadingIcon={
                                    kind === 'phone' ? PhoneIcon : EnvelopeIcon
                                }
                                value={shown ? formatContact(shown) : ''}
                                placeholder={t(
                                    'accountAccess.contacts.notAdded',
                                )}
                                aria-label={label}
                                readOnly
                            />
                            {/* The action sits inside the field so each row reads as one control. */}
                            {!readOnly && data && (
                                <button
                                    type="button"
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-woodyBrown hover:underline underline-offset-2"
                                    onClick={() => setEditing(kind)}
                                >
                                    {actionLabel(kind)}
                                </button>
                            )}
                        </div>
                    </div>
                );
            })}
            <FormDialog
                visible={Boolean(editing)}
                title={
                    editing
                        ? t(
                              `accountAccess.contacts.${
                                  confirmed(editing)
                                      ? 'change'
                                      : unconfirmed(editing)
                                      ? 'confirm'
                                      : 'add'
                              }${editing === 'phone' ? 'Phone' : 'Email'}`,
                          )
                        : ''
                }
                onClose={() => setEditing(undefined)}
            >
                {editing && (
                    <ChangeContactPanel
                        kind={editing}
                        current={confirmed(editing)}
                        unconfirmed={unconfirmed(editing)}
                        onSaved={handleSaved}
                        onCancel={() => setEditing(undefined)}
                    />
                )}
            </FormDialog>
        </>
    );
};
